import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import moment from "moment";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { chain, get, isEmpty, map } from "lodash";
import * as yup from "yup";
import { emailValidator } from "utils/fieldValidators";

import Alert from "components/Alert";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";

import { createInvoiceForCustomer, getPricingData } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { formatCurrency } from "utils/number";
import { ScrollToFieldError, zeroToNull } from "utils/form";
import type { RootState } from "types/state";
import type { CreateInvoiceForCompanyMutation } from "types/graphql";

function validateHasSubscriptionOrPerUserFee(this: yup.TestContext) {
  const parent = this.parent as {
    selectedPerUserFeeId?: string;
    selectedSubscriptionId?: string;
  };
  const hasPerUserFee =
    !isEmpty(parent.selectedPerUserFeeId) &&
    parent.selectedPerUserFeeId !== "0";
  const hasSelectedSubscription =
    !isEmpty(parent.selectedSubscriptionId) &&
    parent.selectedSubscriptionId !== "0";

  return hasPerUserFee || hasSelectedSubscription;
}

const EARLIEST_INVOICE_DATE = moment();
const LATEST_INVOICE_DATE_MONTHLY = moment().add(1, "months").toDate();
const LATEST_INVOICE_DATE_YEARLY = moment().add(1, "years").toDate();
const schema = yup.object({
  billingContactEmail: emailValidator("You must enter a valid email address")
    .label("Billing Contact Email")
    .required(),

  selectedSubscriptionId: yup
    .string()
    .label("Monthly Pricing")
    .test(
      "selectedSubscriptionId or selectedPerUserFeeId",
      "selectedSubscriptionId or selectedPerUserFeeId is required",
      validateHasSubscriptionOrPerUserFee
    ),
  selectedPerUserFeeId: yup
    .string()
    .label("Per User Fee")
    .test(
      "selectedSubscriptionId or selectedPerUserFeeId",
      "selectedSubscriptionId or selectedPerUserFeeId is required",
      validateHasSubscriptionOrPerUserFee
    ),
  selectedSetupFeeId: yup.string().label("Setup Fee").required(),
  anchorBillingDate: yup.date().required(),
  discountPercent: yup
    .number()
    .label("Discount Percent")
    .max(100, "Discount Percent must be less than or equal to 100"),
  discountLength: yup
    .string()
    .label("Discount Length")
    .when("discountPercent", {
      is: (discountPercent: number) => discountPercent > 0,
      then: (s) => s.required(),
    }),
});

const noFee = {
  id: 0,
  unitAmount: 0,
};

const YEAR = "year";
const MONTH = "month";

const DISCOUNT_LENGTHS = [
  {
    value: "ONE_YEAR",
    label: "One Year",
  },
  {
    value: "TWO_YEARS",
    label: "Two Years",
  },
  {
    value: "FOREVER",
    label: "Forever",
  },
];

const DISCOUNT_PERCENTAGES = [
  {
    value: 0,
    label: "0%",
  },
  {
    value: 5,
    label: "5%",
  },
  {
    value: 10,
    label: "10%",
  },
];

const errorSelector = createErrorSelector([
  adminConstants.GET_PRICING_SHEET,
  adminConstants.CREATE_BILLING_INVOICE,
]);
const loadingSelector = createLoadingSelector(
  adminConstants.CREATE_BILLING_INVOICE
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  getPricingData,
  createInvoiceForCustomer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

/** Caller-supplied props: this is a child form embedded by the employer page. */
interface OwnProps {
  companyId: string;
  billingContactEmail: string;
  onSuccess: (
    data: CreateInvoiceForCompanyMutation["createInvoiceForCompany"]
  ) => void;
  onClose: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

/** A single pricing row held in state — a getPricingSheet entry or `noFee`. */
interface PricingOption {
  id: string | number;
  unitAmount: number;
  recurring?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

interface State {
  initialFetching: boolean;
  setupFees: PricingOption[];
  subscriptions: PricingOption[];
  perUserFees: PricingOption[];
  billingFrequency?: string;
}

/** Formik values — mirrors initialValues exactly (anchorBillingDate is a Date,
 * discountPercent a number; the rest are strings/bool). */
interface FormValues {
  selectedSubscriptionId: string;
  selectedSetupFeeId: string;
  selectedPerUserFeeId: string;
  billingContactEmail: string;
  anchorBillingDate: Date;
  billedInArrears: boolean;
  discountPercent: number;
  discountLength: string;
}

class BillingForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      setupFees: [],
      subscriptions: [],
      perUserFees: [],
    };
  }

  async componentDidMount() {
    // dispatchFullLifecycle resolves to a success | failure action union; the
    // original reads the success `.data` (the getPricingSheet payload) directly
    // and spreads each list unguarded (throws if the list is null/absent). The
    // schema rows match PricingOption; cast through unknown to that shape.
    const {
      data: { setupFees, subscriptions, perUserFees },
    } = (await this.props.getPricingData(
      this.props.client as ApolloClient<object>
    )) as unknown as {
      data: {
        setupFees: PricingOption[];
        subscriptions: PricingOption[];
        perUserFees: PricingOption[];
      };
    };

    this.setState({
      setupFees: [...setupFees, noFee],
      perUserFees: [...perUserFees, noFee],
      subscriptions: [...subscriptions, noFee],
      initialFetching: false,
      billingFrequency: YEAR,
    });
  }

  _onSubmit = (vals: FormValues) => {
    const anchorBillingDate = moment(vals.anchorBillingDate).format(
      "YYYY-MM-DD"
    );

    // in the case where we aren't applying a setup fee, just pass in null
    const setupFeeId = zeroToNull(vals.selectedSetupFeeId);
    const perUserFeeId = zeroToNull(vals.selectedPerUserFeeId);
    const subscriptionId = zeroToNull(vals.selectedSubscriptionId);
    const discountPercent = zeroToNull(vals.discountPercent);

    const payload = {
      billingContactEmail: vals.billingContactEmail,
      billedInArrears: vals.billedInArrears,
      companyId: this.props.companyId,
      subscriptionId,
      setupFeeId,
      perUserFeeId,
      anchorBillingDate,
      discountPercent,
      discountLength: discountPercent ? vals.discountLength : null,
    };

    void this.props
      .createInvoiceForCustomer(
        this.props.client as ApolloClient<object>,
        // zeroToNull yields number | string | null and companyId is the own
        // prop; the runtime payload matches the mutation variables shape.
        payload as Parameters<typeof createInvoiceForCustomer>[1]
      )
      .then((result) => {
        if (result.success) {
          toast.success("Successfully created billing subscription.");
          this.props.onSuccess(result.data!);
        }
      });
  };

  subscriptionOptions = () => {
    return chain(this.state.subscriptions)
      .filter((subscription) => {
        const subInterval = get(subscription, "recurring.interval");
        return subInterval === this.state.billingFrequency;
      })
      .map((subscription) => {
        return (
          <option value={subscription.id} key={subscription.id}>
            {formatCurrency(subscription.unitAmount)}
          </option>
        );
      })
      .value();
  };

  discountLengthOptions = () => {
    return map(DISCOUNT_LENGTHS, (discountLength) => {
      return (
        <option value={discountLength.value} key={discountLength.value}>
          {discountLength.label}
        </option>
      );
    });
  };

  discountOptions = () => {
    return map(DISCOUNT_PERCENTAGES, (discountLength) => {
      return (
        <option value={discountLength.value} key={discountLength.value}>
          {discountLength.label}
        </option>
      );
    });
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }
    return (
      <div className="billing-form">
        <Formik<FormValues>
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={this._onSubmit}
          enableReinitialize={true}
          initialValues={{
            selectedSubscriptionId: "",
            selectedSetupFeeId: "",
            selectedPerUserFeeId: "",
            billingContactEmail: this.props.billingContactEmail,
            anchorBillingDate: EARLIEST_INVOICE_DATE.clone()
              .add("1", "days")
              .toDate(),
            billedInArrears: false,
            discountPercent: 0,
            discountLength: "",
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            handleBlur,
            setFieldValue,
          }: FormikProps<FormValues>) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Billing Frequency</Form.Label>
                </Col>
                <Form.Group
                  controlId="formAccountType"
                  as={Row}
                  style={{ paddingLeft: 24 }}
                >
                  <Col md={3}>
                    <Form.Check
                      type="radio"
                      name="billingFrequency"
                      id="yearly"
                      label="Annual"
                      value={YEAR}
                      onChange={(e) => {
                        this.setState({ billingFrequency: e.target.value });
                      }}
                      checked={this.state.billingFrequency === YEAR}
                    />
                  </Col>
                  <Col md={2} />
                  <Col md={3}>
                    <Form.Check
                      type="radio"
                      name="billingFrequency"
                      id="monthly"
                      label="Monthly"
                      value={MONTH}
                      onChange={(e) => {
                        this.setState({ billingFrequency: e.target.value });
                      }}
                      checked={this.state.billingFrequency === MONTH}
                    />
                  </Col>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} md={4} sm={12} controlId="formBillingDate">
                  <Form.Label className="d-block">First Due Date</Form.Label>
                  <DatePicker
                    className="form-control"
                    name="anchorBillingDate"
                    selected={
                      values.anchorBillingDate &&
                      new Date(values.anchorBillingDate)
                    }
                    dateFormat="MM/dd/yyyy"
                    onChange={(val: Date | null) => {
                      void setFieldValue("anchorBillingDate", val);
                    }}
                    onBlur={handleBlur}
                    includeDateIntervals={[
                      {
                        start: EARLIEST_INVOICE_DATE.toDate(),
                        end:
                          this.state.billingFrequency === MONTH
                            ? LATEST_INVOICE_DATE_MONTHLY
                            : LATEST_INVOICE_DATE_YEARLY,
                      },
                    ]}
                  />

                  {/* found-not-fixed: feedback is bound to errors.dob, a field
                      that does not exist on this form (always empty). */}
                  {/* @ts-expect-error dob is not a FormValues field (pre-existing) */}
                  <Form.Control.Feedback>{errors.dob}</Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formSubscription">
                  <Form.Label>
                    {this.state.billingFrequency === MONTH
                      ? "Monthly"
                      : "Yearly"}{" "}
                    Pricing
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                    sm={4}
                    name="selectedSubscriptionId"
                    value={values.selectedSubscriptionId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.selectedSubscriptionId &&
                      !!errors.selectedSubscriptionId
                    }
                    isValid={
                      touched.selectedSubscriptionId &&
                      !errors.selectedSubscriptionId
                    }
                  >
                    <option value="" disabled>
                      Select Subscription
                    </option>
                    {this.subscriptionOptions()}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.selectedSubscriptionId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formPerUser">
                  <Form.Label>Per User Pricing</Form.Label>
                  <Form.Select
                    // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                    sm={4}
                    name="selectedPerUserFeeId"
                    value={values.selectedPerUserFeeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.selectedPerUserFeeId &&
                      !!errors.selectedPerUserFeeId
                    }
                    isValid={
                      touched.selectedPerUserFeeId &&
                      !errors.selectedPerUserFeeId
                    }
                  >
                    <option value="" disabled>
                      Select Per User Fee
                    </option>
                    {this.state.perUserFees.map((fee) => (
                      <option value={fee.id} key={fee.id}>
                        {formatCurrency(fee.unitAmount)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.selectedPerUserFeeId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formSetupFee">
                  <Form.Label>Setup Fee</Form.Label>
                  <Form.Select
                    // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                    sm={4}
                    name="selectedSetupFeeId"
                    value={values.selectedSetupFeeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.selectedSetupFeeId && !!errors.selectedSetupFeeId
                    }
                    isValid={
                      touched.selectedSetupFeeId && !errors.selectedSetupFeeId
                    }
                  >
                    <option value="" disabled>
                      Select One-Time Fee
                    </option>
                    {this.state.setupFees.map((fee) => (
                      <option value={fee.id} key={fee.id}>
                        {formatCurrency(fee.unitAmount)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.selectedSetupFeeId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col} md={4} controlId={"discountPercent"}>
                  <Form.Label>Discount Percent</Form.Label>
                  <Form.Select
                    // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                    sm={4}
                    name="discountPercentage"
                    value={values.discountPercent}
                    onChange={(e) => {
                      void setFieldValue("discountPercent", +e.target.value);
                    }}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.discountPercent && !!errors.discountPercent
                    }
                    isValid={touched.discountPercent && !errors.discountPercent}
                  >
                    <option value="" disabled>
                      Select Discount Percentage
                    </option>
                    {this.discountOptions()}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.discountPercent}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              {values.discountPercent > 0 && (
                <Row className="mb-3">
                  <Form.Group as={Col} sm={6} controlId="discountLength">
                    <Form.Label>Discount Length</Form.Label>
                    <Form.Select
                      // @ts-expect-error sm is not a Form.Select prop (inert; preserved)
                      sm={4}
                      name="discountLength"
                      value={values.discountLength}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.discountLength && !!errors.discountLength
                      }
                      isValid={touched.discountLength && !errors.discountLength}
                    >
                      <option value="" disabled>
                        Select Discount Length
                      </option>
                      {this.discountLengthOptions()}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.discountLength}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              )}
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formBasicUserEmail">
                  <Form.Label>Billing Contact Email</Form.Label>
                  <Form.Control
                    name="billingContactEmail"
                    placeholder="Billing Contact Email"
                    value={values.billingContactEmail}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={
                      touched.billingContactEmail &&
                      !!errors.billingContactEmail
                    }
                    isValid={
                      touched.billingContactEmail && !errors.billingContactEmail
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.billingContactEmail}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group
                  as={Col}
                  sm={6}
                  controlId="formBasicBilledInArrears"
                >
                  <Form.Check
                    type="checkbox"
                    id={"billedInArrears"}
                    name={"billedInArrears"}
                    checked={values.billedInArrears}
                    disabled={false}
                    label={"Billed in arrears"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </Row>
              <Form.Group className="mb-3">
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
                <div className="btn-row">
                  <Button
                    onClick={this.props.onClose}
                    btnLabel={"Cancel"}
                    color={"cancel"}
                    name="cancel"
                  />
                  <Button
                    btnLabel="Send Invoice"
                    name="submit"
                    withArrow={true}
                    loading={this.props.isSubmitting}
                  />
                </div>
              </Form.Group>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(BillingForm));
